/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import * as _ from 'underscore';
import { Logger, MessageService } from '@core/lib/services';
import { Injectable } from '@angular/core';
import { NotifyService } from '@app/common/notify.service';
import { TranslateService } from '@ngx-translate/core';
import { DiQueue } from '@app/di/data-model/di-queue';
import { DiService } from '@app/di/di.service';
import { LsMessage } from '@app/di/data-model/ls-message';
import { DiConfigService } from '@app/di/di-config.service';
import { ReportContextService } from '@app/di/integrations/reporting/report-context.service';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { appConstants } from '@app/common/app-constants';
import { ModalService } from '@core/lib/services/modal.service';
import { EventMessage, Events, EventService } from '@app/common/event';
import { EventWorkItem } from '@app/data-model/event-work-item';
import { ModalRef } from '@wfm/core';
import { NPRDataService } from '@app/di/integrations/utils/npr-bridge.service';
import { REPORT_STATUS } from '@app/di/integrations/reporting/reporting-contants';
import { NPRAuditService } from '../utils//npr-audit.service';
import { CurrentExamContextService } from '@app/common/current-exam-context.service';
import { ExternalAppContextService } from '@app/di/external-app-context.service';
import { TabWorkListService } from '@app/ui/tabs/tab-work-list.service';
import { XmlTemplateManagerService } from '@app/di/channel/lineshaft/xml-template-manager.service';
import { AppConfigService } from '@app/common/app-config.service';
import { UtilService } from '@app/util/util.service';

const log = new Logger('ReportIntegrationService');

@Injectable({
  providedIn: 'root'
})
export class ReportSupportService {
  private ReportingMessageQueueParams = new DiQueue();
  private applicationTerminationStatus = false;
  private setContextTimeout = null;
  private setContextTimeoutCallback;
  private hideAllOpenViewersCallback;
  private instance;
  private lineShaftTerminated = false;
  private createReportPromise = undefined;
  private setBidirectionalPromise = undefined;

  private REPORTING_CONFIG;
  private moduleName;
  private ownerInstance;
  private REPORT_SAVED_TOPIC;
  private REPORT_AUTO_SAVED_TOPIC;

  public workingItem;

  constructor(
    private translate: TranslateService,
    private notify: NotifyService,
    private diConfigService: DiConfigService,
    private diService: DiService,
    private reportContextService: ReportContextService,
    private userPreferenceService: UserPreferenceService,
    private integrationStatusService: IntegrationStatusService,
    private modalService: ModalService,
    private eventService: EventService,
    private dataService: NPRDataService,
    private nprAuditService: NPRAuditService,
    private currentExamContext: CurrentExamContextService,
    private externalAppContextService: ExternalAppContextService,
    private tabWorklistService: TabWorkListService,
    private appConfigService: AppConfigService,
    private xmlTemplateManagerService: XmlTemplateManagerService,
    private utilService: UtilService,
    private messageService: MessageService
  ) {
    this.subscribeEvents();
  }

  initialize() {
    this.REPORTING_CONFIG = this.diConfigService.REPORTING_CONFIG;
    this.moduleName = this.REPORTING_CONFIG.MODULENAME;
    this.ownerInstance = this.REPORTING_CONFIG.OWNERINSTANCE;
    this.REPORT_SAVED_TOPIC = this.ownerInstance + '.' + this.moduleName + '.REPORT_SAVED';
    this.REPORT_AUTO_SAVED_TOPIC = this.ownerInstance + '.' + this.moduleName + '.REPORT_AUTO_SAVED';
  }

  /**
   * The responsibility of this method is
   * 1. Send request to reporting plugin if no report response is pending
   * 2. If any report response is pending push the request to report message queue
   * @param xml - request xml
   * @param accession - exam identifier for report
   * @param action - Action type
   */
  invokeAPIWrapper(xml, accession, action?) {
    const lsMessage = new LsMessage(this.diService.getLSMessageType().SETCONTEXT, xml);
    lsMessage.accession = accession || '';
    lsMessage.action = action || '';
    // LoadMessageQueue.messageWaitingForResponse is checked to process the setcontext requests only after
    // completing the load requests in queue.

    if (
      this.ReportingMessageQueueParams.messageWaitingForResponse === undefined &&
      this.diService.getLoadMessageQueue().messageWaitingForResponse === undefined
    ) {
      log.info('calling invokeAPIWrapperExecute from invokeAPIWrapper');
      this.ReportingMessageQueueParams.messageWaitingForResponse = lsMessage;
      this.diService.invokeAPIWrapperExecute(lsMessage.message);
      this.startSetContextTimeout();
    } else {
      // put on queue
      this.ReportingMessageQueueParams.messageQueue.push(lsMessage);
      log.info(
        'invokeAPIWrapper - Add to queue - queue count: ' + this.ReportingMessageQueueParams.messageQueue.length
      );
    }
  }

  invokePriorityMessage(xml, accession, action) {
    const lsMessage = new LsMessage(this.diService.getLSMessageType().SETCONTEXT, xml);
    lsMessage.accession = accession || '';
    lsMessage.action = action || '';
    this.ReportingMessageQueueParams.messageWaitingForResponse = lsMessage;
    this.diService.invokeAPIWrapperExecute(lsMessage.message);
    this.startSetContextTimeout();
  }

  /**
   * The responsibility of this method is to:
   * 1. Set timeout for report setcontext response
   * 2. Invoke callback of corresponding service
   */
  private startSetContextTimeout() {
    log.debug(
      'setPSSetContextTimeout : setting timeout for report setcontext response : ' +
        this.REPORTING_CONFIG.SETCONTEXT_TIMEOUT
    );
    // Timeout when setcontext response from reporting is pending
    this.setContextTimeout = setTimeout(() => {
      this.setContextTimeoutCallback();
    }, this.REPORTING_CONFIG.SETCONTEXT_TIMEOUT);
  }

  getSavePreference() {
    const savePreference = this.userPreferenceService.getUserPreferenceFromCache('save-report-on-tab-closure');
    return savePreference;
  }

  setApplicationTerminateStatus(status) {
    this.applicationTerminationStatus = status;
  }

  getApplicationTerminateStatus() {
    return this.applicationTerminationStatus;
  }

  setLineShaftTerminateStatus(status) {
    this.lineShaftTerminated = status;
  }

  getLineShaftTerminateStatus() {
    return this.lineShaftTerminated;
  }

  /**
   * Read the Auto minimize dictation preference from user preferences
   */
  getAutoMinimizePreference() {
    return this.userPreferenceService.getUserPreferenceFromCache('autoMinimizeDictation');
  }

  /**
   * The responsibility of this method is to process the report requests from queue
   */
  processQueue() {
    if (this.ReportingMessageQueueParams.messageWaitingForResponse === undefined) {
      const lsMessage =
        this.ReportingMessageQueueParams.messageQueue.length > 0
          ? this.ReportingMessageQueueParams.messageQueue.shift()
          : null;
      if (lsMessage !== null) {
        this.ReportingMessageQueueParams.messageWaitingForResponse = lsMessage;
        log.info('processQueue - calling invokeAPIWrapperExecute from processQueue');
        this.diService.invokeAPIWrapperExecute(lsMessage.message);
        this.startSetContextTimeout();
      } else {
        log.info('processQueue - no message');
      }
    }
  }

  clearMessageQueue() {
    this.ReportingMessageQueueParams.messageQueue = [];
  }

  setHideAllOpenViewersCallback(callback) {
    this.hideAllOpenViewersCallback = callback;
  }

  hideAllOpenViewers() {
    this.hideAllOpenViewersCallback();
  }

  setSetContextTimeoutCallback(callback) {
    this.setContextTimeoutCallback = callback;
  }

  clearSetContextTimeout() {
    clearTimeout(this.setContextTimeout);
  }

  getAccInMsgWaitingForResponse() {
    return (
      !!this.ReportingMessageQueueParams.messageWaitingForResponse &&
      this.ReportingMessageQueueParams.messageWaitingForResponse.accession
    );
  }

  getMessageWaitingForResponse() {
    return this.ReportingMessageQueueParams.messageWaitingForResponse;
  }

  isOpenReportRequestPresentInQueue() {
    const lsMessage =
      this.ReportingMessageQueueParams.messageQueue.length > 0
        ? this.ReportingMessageQueueParams.messageQueue[0]
        : null;

    return !_.isNull(lsMessage) && lsMessage.accession !== '';
  }

  getMessageQueueParams() {
    return this.ReportingMessageQueueParams;
  }

  clearMessageWaitingForResponse() {
    this.ReportingMessageQueueParams.messageWaitingForResponse = undefined;
  }

  private reportIsOpenedButNotAcknowledged(accessionNumber) {
    return !!this.getMessageWaitingForResponse() && this.getMessageWaitingForResponse().accession === accessionNumber;
  }

  private accessionOfOpenedButNotAcknowledgedExam() {
    return this.getMessageWaitingForResponse() && this.getMessageWaitingForResponse().accession;
  }

  private anotherReportIsOpenedButNotAcknowledged(accessionNumber) {
    return !!this.getMessageWaitingForResponse() && this.getMessageWaitingForResponse().accession !== accessionNumber;
  }

  /**
   * Method to find primary exam from associated exams list
   */
  findPrimaryExam(topicName, accNumArray) {
    const currentAccession = this.reportContextService.getCurrentAccession();
    const previousAccession = this.reportContextService.getPreviousAccession();
    for (let i = 0; i < accNumArray.length; i++) {
      log.debug('findPrimaryExam - accNumArray[' + i + '] : ' + accNumArray[i]);
      if (topicName === this.REPORT_SAVED_TOPIC && currentAccession === accNumArray[i]) {
        log.debug('findPrimaryExam - set currentAccNum as primary + ' + currentAccession);
        return currentAccession;
      } else if (topicName === this.REPORT_AUTO_SAVED_TOPIC && previousAccession === accNumArray[i]) {
        log.debug('findPrimaryExam - set prevAccNum as primary + ' + previousAccession);
        return previousAccession;
      }
    }
    return undefined;
  }

  showReportingCloseConfirmationDialog() {
    return this.notify.decision(this.translate.instant('reporting.reportingCloseContent'), {
      headerContent: this.translate.instant('reporting.reportingCloseTitle'),
      noButtonLabel: this.translate.instant('label.dictation'),
      yesButtonLabel: this.translate.instant('label.relaunch'),
      isDecisionModal: true,
      enableYesBtn: true,
      modalClass: 'modal--medium'
    });
  }

  private openReportStuckModal(accession) {
    const modalInstance: ModalRef = this.modalService.openModal(
      this.translate.instant('di.desktopIntegration.warning.reportFailedToFinishOpening').replace(/ACC#/g, accession),
      {
        headerContent: this.translate.instant('di.desktopIntegration.title')
      }
    );

    const unregister = this.getMessageQueueParams().onMessageResponseReceived(() => {
      modalInstance.close();
    });

    const afterClosed = modalInstance.afterClosed.subscribe(() => {
      unregister();
      afterClosed.unsubscribe();
    });

    const afterOpened = modalInstance.afterOpened.subscribe(() => {
      afterOpened.unsubscribe();
      if (!this.reportIsOpenedButNotAcknowledged(accession)) {
        modalInstance.close();
      }
    });
  }

  checkReportContext(actionName, studyId) {
    let accession = this.reportContextService.getStudyIdentifierOfGivenContext(studyId);
    const testForStuck =
      actionName === 'closediscard'
        ? this.reportIsOpenedButNotAcknowledged(accession)
        : this.anotherReportIsOpenedButNotAcknowledged(accession);

    log.info(
      'checkReportContext - action : ' +
        actionName +
        ', requestedProcedureId : ' +
        studyId +
        ', and accession : ' +
        (accession || 'unknown')
    );

    if (testForStuck) {
      this.integrationStatusService.reportStatus = false;
      accession = this.accessionOfOpenedButNotAcknowledgedExam();

      if (!accession) {
        log.info('checkReportContext - save or cancel response pending, continuing the action');
        return false;
      }

      const stuckId = this.reportContextService.getContextCorrespondingToAccessionNum(accession);
      log.info('checkReportContext : report is stuck for accession : ' + accession + ', study id : ' + stuckId);

      this.eventService.sendEvent(
        new EventMessage(Events.read, new EventWorkItem(stuckId, appConstants.reportReOpenParams))
      );

      // Clear the report setcontext timeout
      this.clearSetContextTimeout();
      // Checking whether any other model instance are already opened
      if (_.isUndefined(this.instance)) {
        this.openReportStuckModal(accession);
      }
      return true;
    }
    return false;
  }

  /**
   * This method split the string and return array of accession numbers
   */
  getAccessionNumbersFromString(str, delimiter) {
    if (!_.isUndefined(str)) {
      const arr = str.split(delimiter);
      if (!_.isUndefined(arr) && arr.length > 0) {
        for (let i = 0, len = arr.length; i < len; i++) {
          arr[i] = arr[i].trim();
        }
        return arr;
      }
    }
    return undefined;
  }

  /**
   * Method to handle report creation in data base while opening the report on Reporting Application
   */
  handleCreateReport(examId, accession) {
    this.createReportPromise = this.dataService.createReport(examId, accession).then(
      response => {
        const reportId = response.results[0].reportId;
        this.reportContextService.setNPRContext(reportId, examId, accession);
        log.info('handleCreateReport - createReport success, report Id : ', reportId);
        this.createReportPromise = undefined;
      },
      err => {
        const description = err?.error?.description ? err.error.description : err.error;
        log.error('handleCreateReport - createReport failed description : ', description);
        this.notify.error(this.translate.instant('di.desktopIntegration.createReportFailed'));
        this.createReportPromise = undefined;
      }
    );
  }

  getCreateReportPromise() {
    return this.createReportPromise;
  }

  /**
   * Method to handle report deletion while discarding the report on Reporting Application
   */
  handleReportDiscard(addendumStatus) {
    const nprContext = this.reportContextService.getNPRContext();
    if (nprContext?.reportId) {
      if (addendumStatus === appConstants.AddendumStatus.Yes) {
        log.info('handleReportDiscard : Addendum report discard, update report status as Final');
        this.updateReportStatus(nprContext.reportId, nprContext.accession, nprContext.examId, REPORT_STATUS.FINAL);
      } else {
        log.info('handleReportDiscard : delete report from database on discard');
        this.dataService.deleteReport(nprContext.reportId, nprContext.accession, nprContext.examId).then(
          _ => {
            log.info('handleReportDiscard : Report delete successful.');
            if (this.currentExamContext.getFullContext() == null) {
              this.nprAuditService.logDeleteReportSuccessEvent(
                this.workingItem,
                appConstants.applicationServerParticipant.NPR
              );
            }
          },
          _ => {
            this.notify.error(this.translate.instant('di.desktopIntegration.deleteReportFailed'));
            if (this.currentExamContext.getFullContext() == null) {
              this.nprAuditService.logDeleteReportFailureEvent(
                this.workingItem,
                appConstants.applicationServerParticipant.NPR
              );
            }
          }
        );
      }
    } else {
      log.error('handleReportDiscard : Report Id is empty, cannot save or delete the report');
    }
  }

  /**
   * Method to handle report status updation
   */
  updateReportStatus(reportId, accession, examId, reportStatus) {
    this.workingItem.status = reportStatus;
    this.dataService.saveReport(reportId, accession, examId, reportStatus).then(
      _ => {
        log.info('updateReportStatus : Report status save successful.');
        if (this.currentExamContext.getFullContext() == null) {
          this.nprAuditService.logSaveReportSuccessEvent(
            this.workingItem,
            appConstants.applicationServerParticipant.NPR
          );
        }
      },
      _ => {
        this.notify.error(this.translate.instant('di.desktopIntegration.updateReportFailed'));
        if (this.currentExamContext.getFullContext() == null) {
          this.nprAuditService.logSaveReportFailureEvent(
            this.workingItem,
            appConstants.applicationServerParticipant.NPR
          );
        }
      }
    );
  }

  /**
   * Method to sync the report status of primary and associated exams
   */
  syncSavedExamsReportStatus(workItem) {
    if (workItem.reportId) {
      let reportStatus = workItem.reportStatus;
      if (reportStatus === REPORT_STATUS.DISCARD_ADDENDUM) {
        reportStatus = REPORT_STATUS.FINAL;
      } else if (reportStatus === REPORT_STATUS.DISCARD) {
        reportStatus = 0;
      }
      let syncedExamsList: Array<number> = workItem.associationList;
      if (syncedExamsList) {
        syncedExamsList.push(workItem.id);
      } else {
        syncedExamsList = [workItem.id];
      }
      this.dataService.syncReportStatus(workItem.reportId, syncedExamsList, reportStatus);
    }
  }

  /**
   * Method to sync the saved exams in databse on receving the save response from Reporting Application
   */
  syncSavedExamsInDB(primaryId, primaryAccession, associationList?) {
    if (appConstants.externalTabId !== primaryId) {
      const syncExamsList = [];
      // Adding primary exam details
      syncExamsList.push({ accessionNumber: primaryAccession, examId: primaryId });
      // Adding associated exam details
      const keys = _.keys(associationList);
      _.each(keys, key => {
        syncExamsList.push({ accessionNumber: key, examId: associationList[key].requestedProcedureId });
      });
      this.dataService.syncAssociation(syncExamsList).then(function (response) {
        log.info('syncSavedExamsInDB - syncAssociation response : ', response);
      });
    }
  }

  /**
   * Method to remove exam from association list stored in database
   */
  removeExamFromAssociation(examId) {
    return this.dataService.removeFromAssociation(examId);
  }

  setBidirectionalContext(primaryExam) {
    this.setBidirectionalPromise = new Promise((resolve, reject) => {
      this.externalAppContextService.setContextByAccession(primaryExam).then(
        (workItem: any) => {
          if (workItem) {
            this.reportContextService.setCurrentContext(workItem.id, primaryExam);
            this.reportContextService.setNPRContextAuditData(workItem);
            // Create report meta data in data base if it is not present.
            const reportId = workItem.reportId ? workItem.reportId : null;
            if (reportId === null) {
              this.handleCreateReport(workItem.id, primaryExam);
            } else {
              this.reportContextService.setNPRContext(reportId, workItem.id, primaryExam);
            }
          }
          this.setBidirectionalPromise = undefined;
          resolve(workItem.id);
        },
        error => {
          this.setBidirectionalPromise = undefined;
          reject(error);
        }
      );
    });
    return this.setBidirectionalPromise;
  }

  getBidirectionalPromise() {
    return this.setBidirectionalPromise;
  }

  /**
   * Method to publish reporting events to third party applications
   */
  publishReportEvents(eventName, accession, reportStatus, tabInterrupted, result) {
    if (this.appConfigService.isReportingEventsEnabled()) {
      const params = {
        eventName: eventName,
        accession: accession,
        reportStatus: reportStatus,
        tabInterruptStatus: tabInterrupted,
        result: result
      };
      log.info('publishReportEvents - sending reporting event with parameters : ', params);
      const reportEventXML = this.xmlTemplateManagerService.getReportEventXml(params);
      this.diService.invokeAPI(reportEventXML);
    }
  }

  /**
   * Method to release the read lock of the exam
   */
  releaseReadLock(examId?) {
    const tab = examId ? this.utilService.getTab(examId) : this.utilService.getActiveTab();
    if (tab) {
      const workItem = new EventWorkItem(tab.id);
      workItem.keepAssignment = true;
      this.eventService.sendEvent(new EventMessage(Events.releaseLock, workItem)).then(() => {
        tab.examContext =
          tab.workItem && tab.workItem.keys && tab.workItem.keys.disableAutoLaunchViewer
            ? Events.preview.name
            : Events.launchreadonly.name;
        this.messageService.broadcast('setExamMode', { id: tab.id, examContext: tab.examContext });
      });
    }
  }

  subscribeEvents() {
    this.messageService.on('publishReportEvents').subscribe(report => {
      this.publishReportEvents(report.event, report.accession, report.status, report.interrupted, report.result);
    });
  }
}
